/**
 * Defera Capital — checkout-status poller.
 *
 * Used by `processPaymentMethod` (and the post-checkout status screen) to
 * recover from "stuck checkout" UX where Stripe charged the customer's deposit
 * but the client never received the success response — network drops, mid-flight
 * tab crashes, ambiguous 402s, etc. The Stripe webhook always backfills the
 * booking from `defera_capital_pending_checkouts`, so the right answer is to
 * poll the new `defera-capital/checkout-status` endpoint until the webhook
 * lands the booking row, then redirect the customer to the dashboard as if the
 * checkout had succeeded normally (because it did).
 *
 * Backoff schedule is intentionally conservative — Stripe webhook latency is
 * usually < 5s, so we poll quickly at first then slow down. The total deadline
 * defaults to ~45s which comfortably covers normal webhook latency without
 * holding the user on a spinner forever.
 */

import { deferaCapitalUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';

/** Same algorithm as the server's tripReference derivation in
 *  supabase/functions/defera-capital-create-subscription/handler.ts:725. Kept in
 *  sync so the client can poll for status WITHOUT having received a server response. */
export function deriveCapitalTripReference(flightId) {
  if (!flightId) return null;
  return String(flightId).slice(-6).toUpperCase();
}

const DEFAULT_INTERVALS_MS = [1000, 1500, 2000, 3000, 4000, 5000, 6000, 8000, 10000];
const DEFAULT_TIMEOUT_MS = 45_000;

/**
 * Poll `defera-capital/checkout-status` until the checkout reaches a terminal
 * state ('confirmed', 'failed' or 'not_found' / pending-stale) or the deadline passes.
 *
 * Returns the final status payload (so callers can branch on `status`). Never
 * throws on transient network errors — those are logged and treated as another
 * "still pending" tick. Callers should treat any thrown error as a hard failure
 * (auth missing, programming bug, etc.).
 *
 * @param {object} args
 * @param {string} args.tripReference - 6-char uppercase reference (deterministic from flight_data.id)
 * @param {string} args.accessToken - Supabase JWT
 * @param {object} [args.options]
 * @param {number} [args.options.timeoutMs=45000]
 * @param {number[]} [args.options.intervalsMs] - per-attempt sleep schedule, last value repeats
 * @param {(status: string, payload: object) => void} [args.options.onProgress]
 * @param {AbortSignal} [args.options.signal]
 * @param {string} [args.options.statusUrl] - Override the polled endpoint. Defaults to the Stripe `defera-capital/checkout-status`. NMI track passes `defera-capital-nmi/checkout-status`.
 * @returns {Promise<{status: string, trip_reference: string, booking_id?: string|null, [k: string]: any}>}
 */
export async function awaitCapitalCheckoutConfirmed({ tripReference, accessToken, options = {} }) {
  if (!tripReference) {
    throw new Error('tripReference is required');
  }
  if (!accessToken) {
    throw new Error('accessToken is required');
  }
  const intervals = options.intervalsMs && options.intervalsMs.length > 0
    ? options.intervalsMs
    : DEFAULT_INTERVALS_MS;
  const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : DEFAULT_TIMEOUT_MS;
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
  const signal = options.signal || null;
  const baseStatusUrl = typeof options.statusUrl === 'string' && options.statusUrl
    ? options.statusUrl
    : deferaCapitalUrl('checkout-status');

  const deadline = Date.now() + timeoutMs;
  let attempt = 0;
  let lastPayload = { status: 'pending', trip_reference: tripReference };

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      return { ...lastPayload, status: lastPayload.status === 'confirmed' ? 'confirmed' : 'aborted' };
    }
    attempt += 1;

    let payload;
    try {
      const url = `${baseStatusUrl}?trip_reference=${encodeURIComponent(tripReference)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${accessToken}` }),
        signal,
      });
      payload = await res.json();
      if (!res.ok) {
        /** Treat any non-2xx as a transient error and keep polling — a 5xx during a
         *  Supabase blip shouldn't end the session prematurely while the webhook may
         *  still be flushing. The deadline guard below stops us going forever. */
        console.warn('checkout-status: non-ok response, continuing to poll', { status: res.status, payload });
      }
    } catch (e) {
      /** Network errors are expected occasionally (suspended laptop, captive portal, etc.).
       *  Log and treat as a still-pending tick. */
      console.warn('checkout-status: fetch threw, continuing to poll', e);
      payload = { status: 'pending', trip_reference: tripReference, _network_error: true };
    }

    lastPayload = payload || lastPayload;
    if (onProgress) {
      try { onProgress(lastPayload.status, lastPayload); } catch (_e) { /* ignore */ }
    }

    /** Terminal states: stop polling. `confirmed` → caller redirects to dashboard.
     *  `not_found` → likely a derivation bug or wrong trip; bubble it up. A pending
     *  row stale beyond the server's PENDING_STALE_AFTER_SECONDS still says "pending"
     *  so we keep polling until our own client-side deadline. */
    if (lastPayload.status === 'confirmed') return lastPayload;
    /** `failed`: the latest attempt failed before its deposit was ever charged
     *  (the server says so only then). Nothing will land, so stop now. */
    if (lastPayload.status === 'failed') return lastPayload;
    if (lastPayload.status === 'not_found' && attempt > 1) {
      /** Allow one false-negative on the very first attempt to ride out a race where
       *  the create-subscription POST is still mid-flight and hasn't upserted the
       *  pending row yet. After that, trust the not_found verdict. */
      return lastPayload;
    }

    const interval = intervals[Math.min(attempt - 1, intervals.length - 1)];
    /** Don't sleep past the deadline. */
    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    await new Promise((r) => setTimeout(r, Math.min(interval, remaining)));
  }

  return { ...lastPayload, status: lastPayload.status === 'confirmed' ? 'confirmed' : 'timeout' };
}
